// src/components/TrophyRoom/LeaderboardCard.js
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  Stack,
  Slide,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  PersonAdd as ReferralIcon
} from '@mui/icons-material';

function LeaderboardCard({ user, rank, currentUserId, index }) {
  const isCurrentUser = user.global_user_id === currentUserId;
  const isPrivate = user.is_private;
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');
  
  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈'; 
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    if (isMobile) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString();
  };

  const getDisplayInfo = () => {
    if (isPrivate && !isCurrentUser) {
      return {
        displayName: user.display_name,
        username: user.username,
        avatar: null,
        avatarText: 'A',
        company: user.company
      };
    }
    return {
      displayName: user.display_name || user.username,
      username: user.username,
      avatar: user.profile_pic,
      avatarText: user.display_name?.charAt(0) || user.username?.charAt(0),
      company: user.company
    };
  };

  const displayInfo = getDisplayInfo();

  return (
    <Slide direction="up" in timeout={300 + (index * 100)} mountOnEnter unmountOnExit>
      <Card 
        sx={{ 
          mb: { xs: 1.5, sm: 2 }, 
          border: isCurrentUser ? 2 : 1,
          borderColor: isCurrentUser ? 'primary.main' : 'divider',
          background: isCurrentUser 
            ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.08), rgba(25, 118, 210, 0.04))' 
            : '#ffffff',
          opacity: isPrivate && !isCurrentUser ? 0.88 : 1,
          borderRadius: { xs: 2, sm: 3 },
          overflow: 'hidden',
          position: 'relative',
          boxShadow: isCurrentUser 
            ? '0 8px 25px rgba(25, 118, 210, 0.15)' 
            : '0 2px 12px rgba(0,0,0,0.08)',
          '&:hover': {
            transform: { xs: 'none', sm: 'translateY(-2px)' },
            boxShadow: { 
              xs: '0 4px 15px rgba(0,0,0,0.12)', 
              sm: '0 8px 25px rgba(0,0,0,0.15)' 
            },
            borderColor: 'primary.main',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Mobile Layout */}
          {isMobile ? (
            <Box>
              {/* Top Row: Rank + User Info */}
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    minWidth: '50px',
                    textAlign: 'center',
                    fontWeight: 800,
                    color: rank <= 3 ? 'primary.main' : 'text.secondary',
                    fontSize: '1.5rem'
                  }}
                >
                  {getRankIcon(rank)}
                </Typography>

                <Avatar 
                  src={displayInfo.avatar} 
                  sx={{ 
                    width: 48, 
                    height: 48, 
                    bgcolor: isPrivate && !isCurrentUser ? 'grey.400' : 'primary.main',
                    opacity: isPrivate && !isCurrentUser ? 0.7 : 1,
                    border: '2px solid',
                    borderColor: rank <= 3 ? 'primary.main' : 'divider'
                  }}
                >
                  {displayInfo.avatarText}
                </Avatar>
                
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="bold"
                      noWrap
                      sx={{ 
                        fontStyle: isPrivate && !isCurrentUser ? 'italic' : 'normal',
                        opacity: isPrivate && !isCurrentUser ? 0.8 : 1,
                        fontSize: '1rem'
                      }}
                    >
                      {displayInfo.displayName}
                    </Typography>
                    {isCurrentUser && (
                      <Chip 
                        label="You" 
                        color="primary" 
                        size="small" 
                        sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                  
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    display="flex" 
                    alignItems="center" 
                    gap={0.5}
                    sx={{ 
                      fontStyle: isPrivate && !isCurrentUser ? 'italic' : 'normal',
                      opacity: isPrivate && !isCurrentUser ? 0.6 : 0.7
                    }}
                  >
                    <BusinessIcon fontSize="inherit" />
                    {displayInfo.company || `${user.user_type === 'creator' ? 'Creator' : 'User'}`}
                  </Typography>
                </Box>
              </Box>

              {/* Points Section */}
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="space-between"
                sx={{
                  bgcolor: 'rgba(255, 193, 7, 0.08)',
                  borderRadius: 2,
                  p: 2,
                  mb: 2
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <TrophyIcon sx={{ color: 'warning.main', fontSize: '1.25rem' }} />
                  <Typography variant="h6" fontWeight="bold" color="warning.main">
                    {user.total_points.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    pts
                  </Typography>
                </Box>
                
                <Box textAlign="right">
                  <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                    <StarIcon fontSize="small" />
                    {user.total_achievements}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                    <ScheduleIcon fontSize="inherit" />
                    {formatDate(user.latest_achievement)}
                  </Typography>
                </Box>
              </Box>

              {/* Recent Achievements - Mobile */}
              {user.recent_achievements && user.recent_achievements.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" mb={1} fontWeight="600">
                    Recent:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {user.recent_achievements.slice(0, 2).map((achievement, idx) => (
                      <Chip
                        key={idx}
                        label={achievement.blitzbolt_title}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ 
                          opacity: isPrivate && !isCurrentUser ? 0.7 : 1,
                          fontSize: '0.75rem',
                          height: '24px'
                        }}
                      />
                    ))}
                    {user.recent_achievements.length > 2 && (
                      <Chip
                        label={`+${user.recent_achievements.length - 2} more`}
                        size="small"
                        variant="outlined"
                        color="default"
                        sx={{ fontSize: '0.75rem', height: '24px' }}
                      />
                    )}
                  </Stack>
                </Box>
              )}

              {/* Privacy indicator for mobile */}
              {isPrivate && !isCurrentUser && (
                <Box mt={1}>
                  <Chip 
                    label="Private Profile" 
                    color="default" 
                    size="small" 
                    variant="outlined"
                    sx={{ opacity: 0.6, fontSize: '0.75rem' }}
                  />
                </Box>
              )}
            </Box>
          ) : (
            /* Desktop/Tablet Layout */
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={3}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      minWidth: { sm: '70px', md: '80px' },
                      textAlign: 'center',
                      fontWeight: 800,
                      color: rank <= 3 ? 'primary.main' : 'text.secondary',
                      fontSize: { sm: '2rem', md: '2.5rem' }
                    }}
                  >
                    {getRankIcon(rank)}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar 
                      src={displayInfo.avatar} 
                      sx={{ 
                        width: { sm: 56, md: 64 }, 
                        height: { sm: 56, md: 64 }, 
                        bgcolor: isPrivate && !isCurrentUser ? 'grey.400' : 'primary.main',
                        opacity: isPrivate && !isCurrentUser ? 0.7 : 1,
                        border: '3px solid',
                        borderColor: rank <= 3 ? 'primary.main' : 'divider'
                      }}
                    >
                      {displayInfo.avatarText}
                    </Avatar>
                    
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography 
                          variant="h6" 
                          fontWeight="bold"
                          sx={{ 
                            fontStyle: isPrivate && !isCurrentUser ? 'italic' : 'normal',
                            opacity: isPrivate && !isCurrentUser ? 0.8 : 1,
                            fontSize: { sm: '1.1rem', md: '1.25rem' }
                          }}
                        >
                          {displayInfo.displayName}
                        </Typography>
                        {isCurrentUser && (
                          <Chip 
                            label="You" 
                            color="primary" 
                            size="small" 
                            sx={{ fontWeight: 'bold' }}
                          />
                        )}
                        {isPrivate && !isCurrentUser && (
                          <Chip 
                            label="Private" 
                            color="default" 
                            size="small" 
                            variant="outlined"
                            sx={{ opacity: 0.6 }}
                          />
                        )}
                      </Box>
                     
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        display="flex" 
                        alignItems="center" 
                        gap={0.5}
                        sx={{ 
                          fontStyle: isPrivate && !isCurrentUser ? 'italic' : 'normal',
                          opacity: isPrivate && !isCurrentUser ? 0.6 : 0.7
                        }}
                      >
                        <BusinessIcon fontSize="inherit" />
                        {displayInfo.company || `${user.user_type === 'creator' ? 'Creator' : 'User'}`}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box textAlign="right" sx={{ minWidth: 'fit-content' }}>
                  <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end" mb={1}>
                    <TrophyIcon sx={{ color: 'warning.main', fontSize: { sm: '1.2rem', md: '1.5rem' } }} />
                    <Typography 
                      variant="h5" 
                      fontWeight="bold" 
                      color="warning.main"
                      sx={{ fontSize: { sm: '1.25rem', md: '1.5rem' } }}
                    >
                      {user.total_points.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      pts
                    </Typography>
                  </Box>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    display="flex" 
                    alignItems="center" 
                    gap={0.5} 
                    justifyContent="flex-end"
                    sx={{ mb: 0.5 }}
                  >
                    <StarIcon fontSize="small" />
                    {user.total_achievements} achievements
                  </Typography>
                  
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    display="flex" 
                    alignItems="center" 
                    gap={0.5} 
                    justifyContent="flex-end"
                  >
                    <ScheduleIcon fontSize="inherit" />
                    Last: {formatDate(user.latest_achievement)}
                  </Typography>
                </Box>
              </Box>

              {user.recent_achievements && user.recent_achievements.length > 0 && (
                <Box mt={3} pt={2}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" mb={1.5} fontWeight="600">
                    Recent Achievements:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {user.recent_achievements.map((achievement, idx) => (
                      <Chip
                        key={idx}
                        icon={<StarIcon />}
                        label={achievement.blitzbolt_title}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ 
                          opacity: isPrivate && !isCurrentUser ? 0.7 : 1,
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            transform: 'scale(1.05)',
                            transition: 'all 0.2s ease'
                          }
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Slide>
  );
}

export default LeaderboardCard;